import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';

const textVariants = cva('', {
  variants: {
    size: {
      xs: 'text-xs/relaxed',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
    },
    tone: {
      default: 'text-foreground',
      primary: 'text-primary',
      secondary: 'text-foreground/80',
      muted: 'text-muted-foreground',
      subtle: 'text-muted-foreground/70',
      destructive: 'text-destructive',
      success: 'text-success',
      warning: 'text-warning',
      info: 'text-info',
    },
    weight: {
      normal: 'font-normal',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    lineClamp: {
      none: '',
      1: 'line-clamp-1',
      2: 'line-clamp-2',
      3: 'line-clamp-3',
      4: 'line-clamp-4',
    },
  },
  defaultVariants: {
    size: 'base',
    tone: 'default',
    weight: 'normal',
    lineClamp: 'none',
  },
});

type TextProps = useRender.ComponentProps<'p'> &
  VariantProps<typeof textVariants> & {
    truncate?: boolean;
  };

const Text = ({
  className,
  size = 'base',
  tone = 'default',
  weight = 'normal',
  lineClamp = 'none',
  truncate,
  render,
  ...props
}: TextProps) => {
  return useRender({
    defaultTagName: 'p',
    props: mergeProps<'p'>(
      {
        className: cn(textVariants({ size, tone, weight, lineClamp }), truncate && 'truncate', className),
      },
      props,
    ),
    render,
    state: {
      slot: 'text',
      size,
      tone,
      weight,
    },
  });
};

export { Text, textVariants };
